import { DateTime, Duration } from 'luxon';
import { timeStringToDuration } from '@shared/utilities/utilites';
import { DutyPeriodResponse } from '../response/duty-period.response';
import { LegView } from './leg.view';
import { DataItem } from '@shared/models/data-item';

/**
 * Duty Period View object. Constructor takes DutyPeriodResponse object and converts it to DutyPeriodView object.
 * Numbers (minutes) from API are converted to Luxon Durations for display formatting. Nulls are given
 * a default value.
 */
export class DutyPeriodView {
  timestamp: DateTime;
  dutyPeriodIndex: number;
  dutyPeriodScheduled: Duration;
  dutyPeriodGreater: Duration;
  dutyPeriodDeadhead: Duration;
  dutyPeriodETime: Duration;
  dutyPeriodFGTime: Duration;
  dutyPeriodPayCredit: Duration;
  dutyPeriodTotal: Duration;
  dutyPeriodActualOnDuty: Duration;
  dutyPeriodFdp: Duration;
  dutyPeriodStartTime: DateTime;
  dutyPeriodEndTime: DateTime;
  layoverStation: string;
  layoverLength: Duration;
  dutyPeriodLegs: number;
  containsNextLeg: boolean;
  legs: LegView[] = [];
  dutyPeriodSummaryItems: DataItem[];
  dutyPeriodPayItems: DataItem[];

  // eslint-disable-next-line complexity
  constructor(dutyPeriodApi: DutyPeriodResponse, timestamp: string) {
    this.timestamp = DateTime.fromISO(timestamp, { zone: 'utc' });
    this.dutyPeriodIndex = dutyPeriodApi.dutyPeriodIndex || 0;
    this.dutyPeriodScheduled = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodScheduled || 0,
    });
    this.dutyPeriodGreater = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodGreater || 0,
    });
    this.dutyPeriodDeadhead = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodDeadhead || 0,
    });
    this.dutyPeriodETime = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodETime || 0,
    });
    this.dutyPeriodFGTime = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodFGTime || 0,
    });
    this.dutyPeriodFGTime = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodFGTime || 0,
    });
    this.dutyPeriodPayCredit = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodPayCredit || 0,
    });
    this.dutyPeriodTotal = Duration.fromObject({
      minutes: dutyPeriodApi.dutyPeriodTotal || 0,
    });
    this.dutyPeriodActualOnDuty = timeStringToDuration(
      dutyPeriodApi.dutyPeriodActualOnDuty || ''
    );
    this.dutyPeriodFdp = Duration.fromObject({
      minute: dutyPeriodApi.dutyPeriodFdp,
    });
    this.layoverStation = dutyPeriodApi.layoverStation || '';
    this.layoverLength = timeStringToDuration(
      dutyPeriodApi.layoverLength || ''
    );
    this.dutyPeriodLegs = dutyPeriodApi.dutyPeriodLegs || 0;
    this.legs = dutyPeriodApi.legs?.map(
      (leg) => new LegView(leg, this.dutyPeriodIndex, timestamp)
    );
    this.dutyPeriodStartTime = DateTime.fromISO(
      dutyPeriodApi.dutyPeriodStartTime || '1900-01-01',
      { zone: 'utc' }
    );
    this.dutyPeriodEndTime = DateTime.fromISO(
      dutyPeriodApi.dutyPeriodEndTime || '1900-01-01',
      { zone: 'utc' }
    );
    this.populateOdlActual();

    this.dutyPeriodSummaryItems = this.createDutySummaryItems();
    this.dutyPeriodPayItems = this.createDutyPaySummaryItems();

    this.containsNextLeg = false;
  }

  // Copy layoverLength from dutyPeriod into odlActual in leg since layoverLength is diplayed on
  // both duty period and leg (as applicable).
  private populateOdlActual(): void {
    if (this.layoverLength.as('minutes') > 0) {
      for (const leg of this.legs) {
        if (leg.isDpEnd) {
          leg.odlActual = this.layoverLength;
        }
      }
    }
  }

  private createDutySummaryItems(): DataItem[] {
    return [
      {
        label: 'On Duty',
        value: this.dutyPeriodActualOnDuty.toFormat('h:mm'),
      },
      { label: 'FDP', value: this.dutyPeriodFdp.toFormat('h:mm') },
    ];
  }

  private createDutyPaySummaryItems(): DataItem[] {
    return [
      {
        label: 'Greater',
        value: this.dutyPeriodGreater.toFormat('h:mm'),
      },
      { label: 'P/C', value: this.dutyPeriodPayCredit.toFormat('h:mm') },
      { label: 'Total', value: this.dutyPeriodTotal.toFormat('h:mm') },
    ];
  }
}
